/**
 * Currency Service
 * 
 * API calls for currency management.
 * SUPER_ADMIN only.
 */

import { apiClient } from './api.service';
import type {
  Currency,
  CreateCurrencyDto,
  UpdateCurrencyDto,
  CurrencyQueryParams,
  CurrencyPaginatedResponse,
} from '@/types/currency.types';

// ============================================================================
// CURRENCY SERVICE
// ============================================================================

class CurrencyService {
  private readonly basePath = '/currencies';

  /**
   * Get paginated list of currencies
   */
  async getAll(
    params?: CurrencyQueryParams,
    opts: { signal?: AbortSignal } = {},
  ): Promise<CurrencyPaginatedResponse> {
    const searchParams = new URLSearchParams();

    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.search) searchParams.set('search', params.search);

    const query = searchParams.toString();
    const endpoint = query ? `${this.basePath}?${query}` : this.basePath;

    const response = await apiClient.get<CurrencyPaginatedResponse>(endpoint, {
      signal: opts.signal,
    });
    return response.data;
  }

  /**
   * Get currency by ID
   */
  async getById(
    id: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Currency> {
    const response = await apiClient.get<Currency>(`${this.basePath}/${id}`, {
      signal: opts.signal,
    });
    return response.data;
  }

  /**
   * Get currency by code
   */
  async getByCode(
    code: string,
    opts: { signal?: AbortSignal } = {},
  ): Promise<Currency> {
    const response = await apiClient.get<Currency>(
      `${this.basePath}/code/${code}`,
      { signal: opts.signal },
    );
    return response.data;
  }

  /**
   * Create new currency
   */
  async create(data: CreateCurrencyDto): Promise<Currency> {
    const response = await apiClient.post<Currency>(this.basePath, data);
    return response.data;
  }

  /**
   * Update currency
   */
  async update(id: string, data: UpdateCurrencyDto): Promise<Currency> {
    const response = await apiClient.patch<Currency>(`${this.basePath}/${id}`, data);
    return response.data;
  }



  /**
   * Delete currency
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete(`${this.basePath}/${id}`);
  }
}

// ============================================================================
// EXPORT SINGLETON
// ============================================================================

export const currencyService = new CurrencyService();
